import React, { useRef, useMemo } from 'react';
import { Search, Pin, FileText, Download, CornerDownRight } from 'lucide-react';

/**
 * Read-only chat viewer for the recording detail page.
 * Displays persisted chat messages from S3.
 *
 * Message format (from chatPersistence.ts):
 * { id, text, userId, userName, timestamp (epoch ms),
 *   image, file, replyTo, isDeleted, isPinned, reactions }
 */
const ChatViewer = ({ messages = [], searchable = true }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const scrollContainerRef = useRef(null);

  const filteredMessages = useMemo(() => {
    return messages
      .filter(msg => !msg.isDeleted)
      .filter(msg =>
        !searchQuery ||
        msg.text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        msg.userName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
  }, [messages, searchQuery]);

  // Speaker color map (same palette as TranscriptViewer)
  const speakerColorMap = useMemo(() => {
    const COLORS = [
      { text: 'text-blue-400', dot: 'bg-blue-400' },
      { text: 'text-emerald-400', dot: 'bg-emerald-400' },
      { text: 'text-purple-400', dot: 'bg-purple-400' },
      { text: 'text-amber-400', dot: 'bg-amber-400' },
      { text: 'text-pink-400', dot: 'bg-pink-400' },
      { text: 'text-cyan-400', dot: 'bg-cyan-400' },
      { text: 'text-orange-400', dot: 'bg-orange-400' },
      { text: 'text-lime-400', dot: 'bg-lime-400' },
      { text: 'text-rose-400', dot: 'bg-rose-400' },
      { text: 'text-teal-400', dot: 'bg-teal-400' },
    ];
    const map = new Map();
    let idx = 0;
    for (const msg of messages) {
      if (msg.userName && !map.has(msg.userName)) {
        map.set(msg.userName, COLORS[idx % COLORS.length]);
        idx++;
      }
    }
    return map;
  }, [messages]);

  const getColor = (userName) =>
    speakerColorMap.get(userName) || { text: 'text-white/70', dot: 'bg-white/70' };

  const formatTime = (epochMs) => {
    const date = new Date(epochMs);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {searchable && (
        <div className="mb-3 relative flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            placeholder="Search chat..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#6a1fbf]/50"
          />
        </div>
      )}

      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
        {filteredMessages.length === 0 ? (
          <p className="text-white/50 text-sm text-center py-8">
            {searchQuery ? 'No matching messages found' : 'No chat messages available'}
          </p>
        ) : (
          filteredMessages.map((msg, idx) => {
            const color = getColor(msg.userName);
            return (
              <div
                key={msg.id || idx}
                className={`p-3 rounded-lg bg-white/5 hover:bg-white/10 border-l-2 transition-all duration-200 ${
                  msg.isPinned ? 'border-yellow-400' : 'border-transparent'
                }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${color.dot}`} />
                    <span className={`text-xs font-medium truncate ${color.text}`}>
                      {msg.userName || 'Unknown'}
                    </span>
                    <span className="text-[10px] text-white/40 flex-shrink-0">
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                  {msg.isPinned && (
                    <Pin className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                  )}
                </div>

                {/* Reply context */}
                {msg.replyTo && (
                  <div className="mb-2 pl-4 border-l-2 border-[#6a1fbf]/30">
                    <div className="flex items-center gap-1 mb-0.5">
                      <CornerDownRight className="w-3 h-3 text-[#6a1fbf]" />
                      <span className="text-[10px] text-[#6a1fbf]">
                        Replying to {msg.replyTo.user || 'someone'}
                      </span>
                    </div>
                    <p className="text-[11px] text-white/40 italic truncate">
                      &ldquo;{msg.replyTo.message || '[Media]'}&rdquo;
                    </p>
                  </div>
                )}

                {/* Image */}
                {msg.image && (
                  <img
                    src={msg.image}
                    alt="Shared"
                    className="rounded-md mb-1.5 max-w-full max-h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => window.open(msg.image, '_blank')}
                  />
                )}

                {/* File attachment */}
                {msg.file && (
                  <div className="mb-1.5 bg-white/5 rounded-md p-1.5 flex items-center gap-1.5">
                    <FileText className="w-5 h-5 text-[#6a1fbf] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-white truncate">{msg.file.name}</p>
                      {msg.file.size && (
                        <p className="text-[9px] text-gray-500">{formatFileSize(msg.file.size)}</p>
                      )}
                    </div>
                    {msg.file.url && (
                      <a
                        href={msg.file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 hover:bg-white/10 rounded-md transition-colors flex-shrink-0"
                        title="Download file"
                      >
                        <Download className="w-4 h-4 text-[#00c853]" />
                      </a>
                    )}
                  </div>
                )}

                {/* Message text */}
                {msg.text && (
                  <p className="text-sm text-white/90 leading-relaxed break-words">
                    {searchQuery ? (
                      <HighlightedText text={msg.text} query={searchQuery} />
                    ) : (
                      msg.text
                    )}
                  </p>
                )}

                {/* Reactions */}
                {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                  <div className="flex gap-1 mt-1.5 flex-wrap">
                    {Object.entries(msg.reactions).map(([emoji, users]) => (
                      <span
                        key={emoji}
                        className="px-1.5 py-0.5 bg-white/5 rounded-full text-[10px] flex items-center gap-0.5"
                        title={Array.isArray(users) ? users.join(', ') : ''}
                      >
                        <span>{emoji}</span>
                        <span className="text-white/40">
                          {Array.isArray(users) ? users.length : 0}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const HighlightedText = ({ text, query }) => {
  if (!query || !text) return text || '';
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className="bg-yellow-400/30 text-yellow-300">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};

export default ChatViewer;
